// src/components/fix-actions/asset-generation-tray-setup.tsx
// SetupModeContent — the configuration form shown before generation starts.
// Lets the user confirm brand context, competitors, deployment targets, tone,
// and risk sensitivity before hitting "Generate Asset →".

import { useState } from "react";
import { X, Plus } from "lucide-react";
import { Button } from "../ui/button";
import type { ArtifactType } from "../../services/fix-actions/optimization-studio-service";
import type { GenerationConfig } from "./asset-generation-tray";

interface SetupModeContentProps {
  objective: string;
  outputTypeDescription: string;
  artifactType: ArtifactType;
  config: GenerationConfig;
  setConfig: (config: GenerationConfig) => void;

  // Optional label overrides for deployment target checkboxes
  ownedSiteLabel?: string;
  thirdPartyBlogsLabel?: string;
  forumsLabel?: string;

  modelsUsed?: string[];
}

const TONE_OPTIONS = ["Neutral", "Authoritative", "Conversational", "Technical", "Empathetic"];
const RISK_OPTIONS = ["Standard", "Conservative", "Aggressive"];

export function SetupModeContent({
  objective,
  outputTypeDescription,
  artifactType,
  config,
  setConfig,
  ownedSiteLabel = "Owned site / documentation",
  thirdPartyBlogsLabel = "Third-party blogs & publishers",
  forumsLabel = "Forums & community platforms",
  modelsUsed,
}: SetupModeContentProps) {
  const [competitorInput, setCompetitorInput] = useState("");

  const addCompetitor = () => {
    const val = competitorInput.trim();
    if (!val) return;
    if (config.competitors.includes(val)) {
      setCompetitorInput("");
      return;
    }
    setConfig({ ...config, competitors: [...config.competitors, val] });
    setCompetitorInput("");
  };

  const removeCompetitor = (name: string) => {
    setConfig({
      ...config,
      competitors: config.competitors.filter((c) => c !== name),
    });
  };

  return (
    <div className="space-y-8">
      {/* Objective summary */}
      <div>
        <div
          style={{
            fontSize: "var(--text-xs)",
            fontWeight: "var(--font-weight-semibold)",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: "var(--muted-foreground)",
            marginBottom: "8px",
          }}
        >
          What you're generating
        </div>
        <div
          className="p-4 rounded-lg"
          style={{
            backgroundColor: "var(--muted)",
            border: "1px solid var(--border)",
          }}
        >
          <div
            style={{
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-weight-semibold)",
              marginBottom: "4px",
            }}
          >
            {outputTypeDescription || artifactType}
          </div>
          {objective && (
            <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", lineHeight: "1.6" }}>
              {objective}
            </p>
          )}
          {!!modelsUsed?.length && (
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                Models used:
              </span>
              {modelsUsed.map((m) => (
                <span
                  key={m}
                  className="px-2 py-0.5 rounded"
                  style={{
                    backgroundColor: "var(--background)",
                    color: "var(--foreground)",
                    fontSize: "var(--text-xs)",
                    border: "1px solid var(--border)",
                  }}
                >
                  {m}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Competitors */}
      <div>
        <label
          style={{
            display: "block",
            fontSize: "var(--text-sm)",
            fontWeight: "var(--font-weight-semibold)",
            marginBottom: "4px",
          }}
        >
          Competitors to reference{" "}
          <span style={{ fontWeight: "var(--font-weight-normal)", color: "var(--muted-foreground)" }}>
            (optional)
          </span>
        </label>
        <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", marginBottom: "8px" }}>
          The asset will position your brand against these competitors where relevant.
        </p>

        {config.competitors.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {config.competitors.map((c) => (
              <span
                key={c}
                className="inline-flex items-center gap-1 px-2 py-1 rounded"
                style={{
                  backgroundColor: "var(--muted)",
                  border: "1px solid var(--border)",
                  fontSize: "var(--text-xs)",
                }}
              >
                {c}
                <button
                  onClick={() => removeCompetitor(c)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    color: "var(--muted-foreground)",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <input
            type="text"
            value={competitorInput}
            onChange={(e) => setCompetitorInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addCompetitor();
              }
            }}
            placeholder="Enter competitor name..."
            className="flex-1 px-3 py-2 border rounded-md"
            style={{
              backgroundColor: "var(--background)",
              borderColor: "var(--border)",
              fontSize: "var(--text-sm)",
            }}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={addCompetitor}
            style={{ borderColor: "var(--border)" }}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>
      </div>

      {/* Deployment targets */}
      <div>
        <label
          style={{
            display: "block",
            fontSize: "var(--text-sm)",
            fontWeight: "var(--font-weight-semibold)",
            marginBottom: "4px",
          }}
        >
          Deployment targets
        </label>
        <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", marginBottom: "12px" }}>
          Where will this content be published? This affects tone and format optimizations.
        </p>

        <div className="space-y-3">
          {(
            [
              {
                key: "ownedSite",
                label: ownedSiteLabel,
                description: "Your website, docs, knowledge base, or help center",
              },
              {
                key: "thirdPartyBlogs",
                label: thirdPartyBlogsLabel,
                description: "Guest posts, syndicated content, partner sites",
              },
              {
                key: "forums",
                label: forumsLabel,
                description: "Reddit, Quora, industry forums, community Q&A",
              },
            ] as const
          ).map(({ key, label, description }) => (
            <label
              key={key}
              className="flex items-start gap-3 cursor-pointer p-3 rounded-lg transition-colors hover:bg-muted/50"
              style={{ border: "1px solid var(--border)" }}
            >
              <input
                type="checkbox"
                checked={config.deploymentTargets[key]}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    deploymentTargets: {
                      ...config.deploymentTargets,
                      [key]: e.target.checked,
                    },
                  })
                }
                style={{ marginTop: "2px", accentColor: "var(--alpha-teal)" }}
              />
              <div>
                <div style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-medium)" }}>
                  {label}
                </div>
                <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                  {description}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Tone */}
      <div>
        <label
          style={{
            display: "block",
            fontSize: "var(--text-sm)",
            fontWeight: "var(--font-weight-semibold)",
            marginBottom: "8px",
          }}
        >
          Tone
        </label>
        <div className="flex flex-wrap gap-2">
          {TONE_OPTIONS.map((tone) => (
            <button
              key={tone}
              onClick={() => setConfig({ ...config, tone })}
              className="px-3 py-1.5 rounded-md transition-colors"
              style={{
                backgroundColor:
                  config.tone === tone ? "var(--alpha-teal)" : "var(--muted)",
                color:
                  config.tone === tone ? "var(--background)" : "var(--foreground)",
                border:
                  config.tone === tone
                    ? "1px solid var(--alpha-teal)"
                    : "1px solid var(--border)",
                fontSize: "var(--text-sm)",
                fontWeight:
                  config.tone === tone
                    ? "var(--font-weight-semibold)"
                    : "var(--font-weight-normal)",
                cursor: "pointer",
              }}
            >
              {tone}
            </button>
          ))}
        </div>
      </div>

      {/* Risk sensitivity */}
      <div>
        <label
          style={{
            display: "block",
            fontSize: "var(--text-sm)",
            fontWeight: "var(--font-weight-semibold)",
            marginBottom: "4px",
          }}
        >
          Risk sensitivity
        </label>
        <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", marginBottom: "8px" }}>
          Controls how cautiously the asset hedges claims and avoids controversies.
        </p>
        <div className="flex gap-2">
          {RISK_OPTIONS.map((risk) => (
            <button
              key={risk}
              onClick={() => setConfig({ ...config, riskSensitivity: risk })}
              className="flex-1 py-2 rounded-md transition-colors"
              style={{
                backgroundColor:
                  config.riskSensitivity === risk ? "var(--signal-blue)" : "var(--muted)",
                color:
                  config.riskSensitivity === risk ? "white" : "var(--foreground)",
                border:
                  config.riskSensitivity === risk
                    ? "1px solid var(--signal-blue)"
                    : "1px solid var(--border)",
                fontSize: "var(--text-sm)",
                fontWeight:
                  config.riskSensitivity === risk
                    ? "var(--font-weight-semibold)"
                    : "var(--font-weight-normal)",
                cursor: "pointer",
              }}
            >
              {risk}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}